import React, { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import Dashboard from "@/components/Dashboard";
import TraceVisualization from "@/components/TraceVisualization";
import ServicesOverview from "@/components/ServicesOverview";
import Reports from "@/components/Reports";
import DataIngestion from "@/components/DataIngestion";
import ServiceDependencyGraph from "@/components/ServiceDependencyGraph";
import ModelStudio from "@/components/ModelStudio";
import RemediationManagement from "@/components/RemediationManagement";
import IntegrationManagement from "@/components/IntegrationManagement";
import IntegrationConfigDemo from "@/components/IntegrationConfigDemo";
import IncidentManagement from "@/components/IncidentManagement";
import ProblemManagement from "@/components/ProblemManagement";
import AdvancedAnalytics from "@/components/AdvancedAnalytics";
import EventAnalysisNoiseReduction from "@/components/EventAnalysisNoiseReduction";
import Login from "@/components/Login";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { 
  Activity, 
  Network, 
  CloudCog, 
  Server, 
  FileText, 
  Database, 
  Share2, 
  Brain, 
  Shield,
  Settings,
  AlertTriangle,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  Bot,
  Filter,
  LogOut,
  User
} from "lucide-react";

const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({
    'predictive-anomaly': false,
    'agentic-ai': false,
    'event-analysis': false
  });

  const toggleExpanded = (itemKey) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemKey]: !prev[itemKey]
    }));
  };

  const navigationStructure = [
    { path: "/", label: "Dashboard", icon: Activity, type: "single" },
    { path: "/model-studio", label: "Model Studio", icon: Brain, type: "single" },
    { path: "/analytics", label: "Analytics", icon: FileText, type: "single" },
    
    {
      key: "agentic-ai",
      label: "Agentic AI",
      icon: Bot,
      type: "parent",
      children: [
        { path: "/incidents", label: "Incidents", icon: AlertTriangle },
        { path: "/problems", label: "Problems", icon: CloudCog }
      ]
    },
    
    {
      key: "predictive-anomaly", 
      label: "Predictive Anomaly Detection",
      icon: TrendingUp,
      type: "parent",
      children: [
        { path: "/services", label: "Services", icon: Server },
        { path: "/traces", label: "Traces", icon: Network },
        { path: "/dependency-graph", label: "Dependencies", icon: Share2 }
      ]
    },
    
    {
      key: "event-analysis",
      label: "Event Analysis & Noise Reduction", 
      icon: Filter,
      type: "parent",
      children: [
        { path: "/event-analysis", label: "Event Analysis", icon: Filter },
      ]
    },
    
    { path: "/data-ingestion", label: "Data Pipeline", icon: Database, type: "single" },
    { path: "/integrations", label: "Integrations", icon: Settings, type: "single" },
    { path: "/remediation", label: "Remediation", icon: Shield, type: "single" }
  ];

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 h-[calc(100vh-4rem)] w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="h-full overflow-y-auto py-4">
          <nav className="space-y-1 px-3">
            {navigationStructure.map((item) => {
              if (item.type === "single") {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    data-testid={`sidebar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              } else if (item.type === "parent") {
                const Icon = item.icon;
                const isExpanded = expandedItems[item.key];
                const hasActiveChild = item.children.some(child => location.pathname === child.path);
                
                return (
                  <div key={item.key} className="space-y-1">
                    <button
                      onClick={() => toggleExpanded(item.key)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                        hasActiveChild
                          ? "bg-blue-50 text-blue-700 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                      data-testid={`sidebar-parent-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm flex-1 text-left">{item.label}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>
                    
                    {isExpanded && (
                      <div className="ml-6 space-y-1">
                        {item.children.map((child) => {
                          const ChildIcon = child.icon;
                          const isChildActive = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              onClick={() => setIsOpen(false)}
                              className={`flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                                isChildActive
                                  ? "bg-blue-50 text-blue-700 font-medium"
                                  : "text-gray-600 hover:bg-gray-50"
                              }`}
                              data-testid={`sidebar-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <ChildIcon className="w-4 h-4" />
                              <span className="text-sm">{child.label}</span>
                              {isChildActive && <ChevronRight className="w-3 h-3 ml-auto" />}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="mt-8 px-3">
            <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
              <h4 className="font-semibold text-sm text-gray-900 mb-1">
                Juno HawkAI Platform
              </h4>
              <p className="text-xs text-gray-600 mb-3">
                Cloud Anomaly Detection and Remediation
              </p>
              <div className="flex items-center text-xs text-gray-500">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse" />
                ML Models Active
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const TopNavigation = ({ toggleSidebar }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  
  const topNavItems = [
    { path: "/", label: "Dashboard", icon: Activity },
    { path: "/model-studio", label: "Model Studio", icon: Brain },
    { path: "/analytics", label: "Analytics", icon: FileText },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-40">
      <div className="flex items-center justify-between h-16 px-4">
        {/* Left: Logo + Menu Toggle */}
        <div className="flex items-center space-x-4">
          <button
            onClick={toggleSidebar}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            data-testid="menu-toggle"
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>

          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-2 rounded-lg">
              <CloudCog className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Juno HawkAI</h1>
              <p className="text-xs text-gray-500 hidden sm:block">
                Cloud Anomaly Detection
              </p>
            </div>
          </div>
        </div>

        {/* Center: Top Navigation Items */}
        <div className="flex items-center space-x-2">
          {topNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                  isActive
                    ? "bg-blue-50 text-blue-700 font-medium"
                    : "text-gray-700 hover:bg-gray-50"
                }`}
                data-testid={`top-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium hidden sm:inline">{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Right: User Info + Status + Logout */}
        <div className="flex items-center space-x-3">
          {/* User Info */}
          <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
            <User className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">{user?.username}</span>
          </div>

          {/* Status Indicator */}
          <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-green-50 rounded-full border border-green-200">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-green-700">Active</span>
          </div>

          {/* Logout Button */}
          <button
            onClick={logout}
            className="flex items-center space-x-2 px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
};

const AppContent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isAuthenticated, loading, login } = useAuth();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-2xl inline-block mb-4">
            <CloudCog className="w-12 h-12 text-white animate-spin" />
          </div>
          <p className="text-gray-600 font-medium">Loading Juno HawkAI...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  // Show main application if authenticated
  return (
    <div className="App min-h-screen bg-gray-50">
      <TopNavigation toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Main Content */}
      <div className="pt-16 lg:pl-64 transition-all duration-300">
        <div className="max-w-7xl mx-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/incidents" element={<IncidentManagement />} />
            <Route path="/problems" element={<ProblemManagement />} />
            <Route path="/services" element={<ServicesOverview />} />
            <Route path="/traces" element={<TraceVisualization />} />
            <Route path="/dependency-graph" element={<ServiceDependencyGraph />} />
            <Route path="/data-ingestion" element={<DataIngestion />} />
            <Route path="/integrations" element={<IntegrationManagement />} />
            <Route path="/integrations/demo" element={<IntegrationConfigDemo />} />
            <Route path="/model-studio" element={<ModelStudio />} />
            <Route path="/remediation" element={<RemediationManagement />} />
            <Route path="/analytics" element={<AdvancedAnalytics />} />
            <Route path="/event-analysis" element={<EventAnalysisNoiseReduction />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
